import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { organizationAPI } from '../utils/api';
import { toast } from 'sonner';

const OrganizationSetup = () => {
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('STANDARD');
  const [loading, setLoading] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await organizationAPI.create({ name, subscription_plan: plan });
      toast.success('Organization created successfully!');
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Failed to create organization');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}}>Create Your Organization</h1>
          <p className="text-[#9E9E9E]">Set up your organization to start managing shows</p>
        </div>

        <div className="bg-[#161616] border border-[#262626] rounded-lg p-8">
          <form onSubmit={handleSubmit} data-testid="org-setup-form" className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Organization Name</label>
              <input
                data-testid="org-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                placeholder="Enter organization name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-4">Subscription Plan</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  data-testid="plan-standard"
                  onClick={() => setPlan('STANDARD')}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    plan === 'STANDARD'
                      ? 'border-[#0096FF] bg-[#0096FF]/10'
                      : 'border-[#262626] hover:border-[#0096FF]/50'
                  }`}
                >
                  <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>Standard</h3>
                  <p className="text-3xl font-black text-[#0096FF] mb-4" style={{fontFamily: 'Outfit'}}>$19.99<span className="text-base text-[#9E9E9E]">/mo</span></p>
                  <ul className="text-sm text-[#9E9E9E] space-y-2">
                    <li>Single-day events</li>
                    <li>Entry management & scoring</li>
                    <li>Payouts & rollovers</li>
                  </ul>
                </button>

                <button
                  type="button"
                  data-testid="plan-professional"
                  onClick={() => setPlan('PROFESSIONAL')}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    plan === 'PROFESSIONAL'
                      ? 'border-[#00D4FF] bg-[#00D4FF]/10'
                      : 'border-[#262626] hover:border-[#00D4FF]/50'
                  }`}
                >
                  <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>Professional</h3>
                  <p className="text-3xl font-black text-[#00D4FF] mb-4" style={{fontFamily: 'Outfit'}}>$39.99<span className="text-base text-[#9E9E9E]">/mo</span></p>
                  <ul className="text-sm text-[#9E9E9E] space-y-2">
                    <li>Multi-day events</li>
                    <li>Stall & RV reservations</li>
                    <li>Everything in Standard</li>
                  </ul>
                </button>
              </div>
            </div>

            <button
              data-testid="org-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSetup;
